import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from "lucide-react";
import { clsx } from "clsx";
import { VISIT_STATUS_LABEL } from "../../../shared/visit-status.ts";
import type { VisitStatus } from "../../../shared/types.ts";
import { ApiError } from "../lib/api.ts";

export const cx = clsx;

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cx("animate-spin", className ?? "w-5 h-5")} aria-hidden />;
}

export function Loading({ text = "Загружаем…" }: { text?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-mutedt text-sm">
      <Spinner /> {text}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
  return (
    <div className="card p-4 flex items-start gap-3 text-err-t bg-err-bg border-err/30">
      <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">{msg}</div>
      {onRetry && (
        <button className="btn-ghost btn-sm" onClick={onRetry}>
          Повторить
        </button>
      )}
    </div>
  );
}

export function Banner({ kind = "info", children, action, icon }: { kind?: "info" | "warn" | "err" | "ok"; children: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  const map = {
    info: "bg-info-bg text-info-t border-violet/20",
    warn: "bg-warn-bg text-warn-t border-warn/30",
    err: "bg-err-bg text-err-t border-err/30",
    ok: "bg-ok-bg text-ok-t border-ok/30",
  };
  const Icon = kind === "err" ? XCircle : kind === "warn" ? AlertTriangle : kind === "ok" ? CheckCircle2 : Info;
  return (
    <div className={cx("rounded-xl border px-3 py-2.5 flex items-start gap-2.5 text-[13.5px]", map[kind])}>
      <span className="mt-0.5 shrink-0">{icon ?? <Icon className="w-4 h-4" />}</span>
      <div className="flex-1 min-w-0">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

const STATUS_STYLE: Record<VisitStatus, string> = {
  draft: "pill-muted",
  recording: "pill-err",
  uploading: "pill-warn",
  recognizing: "pill-violet",
  recognition_failed: "pill-err",
  needs_review: "pill-warn",
  ready: "pill-ok",
  completed: "pill-ok",
  transferred: "pill-muted",
};

export function StatusPill({ status, waiting }: { status: VisitStatus; waiting?: boolean }) {
  return (
    <span className="inline-flex gap-1 flex-wrap">
      <span className={STATUS_STYLE[status]}>
        {status === "recording" && <span className="rec-dot !w-2 !h-2" />}
        {VISIT_STATUS_LABEL[status]}
      </span>
      {waiting && <span className="pill-warn">ждёт сети</span>}
    </span>
  );
}

export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-[2px] flex items-end sm:items-center justify-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal className={cx("bg-card w-full rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92dvh] flex flex-col", wide ? "sm:max-w-3xl" : "sm:max-w-lg")}>
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-line">
          <h2 className="text-[16px] font-bold">{title}</h2>
          <button className="p-2 -mr-2 text-mutedt hover:text-ink min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={onClose} aria-label="Закрыть">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 sm:px-5 py-4 overflow-y-auto">{children}</div>
        {footer && <div className="px-4 sm:px-5 py-3 border-t border-line flex flex-wrap justify-end gap-2 pb-[max(12px,env(safe-area-inset-bottom))]">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Confirm({
  open,
  title,
  text,
  confirmText = "Подтвердить",
  danger,
  reasonLabel,
  reasonRequired,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  text?: ReactNode;
  confirmText?: string;
  danger?: boolean;
  reasonLabel?: string;
  reasonRequired?: boolean;
  onConfirm: (reason: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    if (open) {
      setReason("");
      setErr(null);
    }
  }, [open]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button
            className={danger ? "btn-danger" : "btn-primary"}
            disabled={busy || (reasonRequired && !reason.trim())}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await onConfirm(reason.trim());
                onClose();
              } catch (e) {
                setErr(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy && <Spinner className="w-4 h-4" />} {confirmText}
          </button>
        </>
      }
    >
      {text && <div className="text-[14px] text-ink mb-3">{text}</div>}
      {reasonLabel && (
        <label className="block">
          <span className="label">{reasonLabel}</span>
          <textarea className="textarea min-h-[80px]" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </label>
      )}
      {err ? <div className="mt-3"><ErrorBox error={err} /></div> : null}
    </Modal>
  );
}

// --- toasts ---
type Toast = { id: number; text: string; kind: "ok" | "err" | "info" };
let pushToast: (t: Omit<Toast, "id">) => void = () => {};
export const toast = (text: string, kind: Toast["kind"] = "ok") => pushToast({ text, kind });

export function Toaster() {
  const [items, setItems] = useState<Toast[]>([]);
  const n = useRef(0);
  useEffect(() => {
    pushToast = (t) => {
      const id = ++n.current;
      setItems((x) => [...x, { ...t, id }]);
      setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), 3500);
    };
  }, []);
  return createPortal(
    <div className="fixed z-[90] bottom-[calc(16px+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 flex flex-col gap-2 w-[min(92vw,380px)] no-print" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={cx("rounded-xl shadow-lg px-4 py-3 text-[14px] font-medium border-l-4 bg-card", t.kind === "err" ? "border-err" : t.kind === "info" ? "border-violet" : "border-ok")}>
          {t.text}
        </div>
      ))}
    </div>,
    document.body,
  );
}

export function Field({ label, children, hint, error }: { label: string; children: ReactNode; hint?: ReactNode; error?: string | null }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint && !error && <span className="block text-[12px] text-mutedt mt-1">{hint}</span>}
      {error && <span className="block text-[12px] text-err mt-1">{error}</span>}
    </label>
  );
}

export function PageTitle({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h1 className="text-[20px] sm:text-[22px] font-bold leading-tight">{title}</h1>
        {subtitle && <div className="text-[13px] text-mutedt mt-0.5">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Empty({ title, text, action }: { title: string; text?: ReactNode; action?: ReactNode }) {
  return (
    <div className="text-center py-12 px-4">
      <div className="text-[15px] font-semibold">{title}</div>
      {text && <div className="text-[13px] text-mutedt mt-1">{text}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { id: T; label: ReactNode }[] }) {
  return (
    <div className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-1" role="tablist">
      {items.map((i) => (
        <button key={i.id} role="tab" aria-selected={value === i.id} className={cx("tab whitespace-nowrap", value === i.id && "tab-active")} onClick={() => onChange(i.id)}>
          {i.label}
        </button>
      ))}
    </div>
  );
}

export function errorText(e: unknown): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
}
